import React from 'react';
import { array, string, func, bool } from 'prop-types';
import styled from 'styled-components';

import withColor from 'util/hoc/with-color';
import { InputEvent as SyntheticEvent } from 'library/helpers/synthetic-events';
import StopSvg from 'library/icons/stop.svg';
import { resolve } from 'library/helpers/path-resolver';

const FileUploadPlaceholder = () => (
  <span className="file-upload-placeholder">
    <i className="icon-doc-text" /> No File Selected
  </span>
);

const StyledFileUpload = withColor(styled.div`
  ${props => props.overrideStyles ? `` : `
    color: ${props => props.colors.grey1};
    display: block;
    width: 100%;
    margin-bottom: 1em;
    font-size: 1em;
    border: none;
    font-weight: 400;
    border: 1px solid #ccc;
    position: relative;
    cursor: pointer;

    &:hover {
        > span:after {
            background: #dedede;
        }
    }
    > span {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        padding: .5em;
        cursor: pointer;
        z-index: 0;
        &:after {
            content: "Choose File";
            height: 1.5em;
            border: 1px solid #ccc;
            display: inline-block;
            border-radius: 4px;
            float: right;
            margin-top: -.15em;
            padding: 0 1em;
            text-align: center;
            background: #efefef;
            color: ${props => props.colors.grey2};
        }
    }
    > input[type=file] {
        opacity: 0;
        margin: 0;
        border: none;
        position: relative;
        z-index: 1;
        cursor: inherit;
        width: 100%;
    }

    &.hover {
        border: 1px dashed ${props => props.colors.secondaryColor};
    }

    &[readOnly] {
        color: ${props => props.colors.grey2};
        -webkit-user-select: none;
        user-select: none;
    }
    `}
`);

const StyledFileItem = styled.div`
  display: flex;
    > :first-child {
      flex: 1;
    }
`;

const StyledRemoveLink = styled.div`
  margin-bottom: .5em;
  width: fit-content;
  text-decoration: none;
  display: flex;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  span {
    margin-left: .25em;
  }
`;

export default class FileUpload extends React.Component {
    static isNativeWrapper = true;

    static propTypes = {
      accept: string,
      multiple: bool,
      files: array,
      readOnly: bool,
      onChange: func,
      onRemove: func,
      name: string,
      required: bool
    }

    static defaultProps = {
      accept: '*',
      multiple: false,
      files: [],
      required: false
    }

    constructor(...params) {
      super(...params);

      this.state = {
        hover: false
      };
    }

    parseSavedFiles = (savedFiles) => {
      return savedFiles.reduce((out, file, i) => {
        let filename = resolve(file, 'filename', '').split('\\').pop().split('/').pop();
        if (filename) {
          out.push(
            <StyledFileItem key={i}>
              {
                file['signed_url']
                  ? <a target='_blank' href={file['signed_url']}>{filename}</a>
                  : <div>{filename}</div>
              }
              {
                this.props.onRemove
                  ? (
                    <StyledRemoveLink
                      onClick={() => this.props.onRemove(file['blobKey'])}
                    >
                      <StopSvg /><span>Remove</span>
                    </StyledRemoveLink>
                  )
                  : null
              }
            </StyledFileItem>
          );
        }
        return out;
      }, []);
    }

    handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.setState((nextState) => {
        nextState.hover = true;
        return nextState;
      });
    }

    handleDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
    }

    handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.setState((nextState) => {
        nextState.hover = false;
        return nextState;
      });
    }

    handleFileInput = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.processFiles(this.input.files);
    }

    handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.processFiles(e.dataTransfer.files);

      this.props.onChange(new SyntheticEvent({
        name: this.props.name,
        files: e.dataTransfer.files,
        value: e.dataTransfer.files[0].name,
        type: 'file'
      }));
    }

    processFiles = (files) => {
      const { multiple, accept } = this.props;
      if (!files.length) {
        // Throw an error
        return;
      }

      if (multiple) {
        for (let i = 0; i < files.length; i++) {
          this.processFileUpload(files.item(i));
        }
      } else {
        this.processFileUpload(files.item(0));
      }
    }

    processFileUpload = (file) => {
      const { accept } = this.props;
      if (accept === '*' || accept.split(',').indexOf(file.type) > -1) {
        this.props.onChange(new SyntheticEvent({
          name: this.props.name,
          files: [file],
          value: file.name,
          type: 'file'
        }));
      }
    }

    renderfilenames = () => {
      return this.parseSavedFiles(this.props.files) || <FileUploadPlaceholder />;
    }

    render() {
      const { overrideStyles, files, readOnly=false, className, onFetched, ...props } = this.props;

      const computedClassName = [
        className,
        this.state.hover ? 'hover' : ''
      ].filter(c=>c).join(' ');

      return (
        <StyledFileUpload overrideStyles={overrideStyles} className={computedClassName} onDragOver={this.handleDragOver} onDragEnter={this.handleDragEnter} onDragLeave={this.handleDragLeave} onDrop={this.handleDrop} readOnly={readOnly}>
          <span onDragOver={this.handleDragOver} onDragEnter={this.handleDragEnter} onDragLeave={this.handleDragLeave}>{this.renderfilenames()}</span>
          <input required={this.props.required && !files.length} readOnly={readOnly} ref={r=>this.input=r} onChange={this.handleFileInput} type="file"/>
        </StyledFileUpload>
      );
    }
}
